import { useMemo, useState } from 'react';
import { Link, Navigate } from 'react-router-dom';
import { useFlow } from '../state/flowStore';
import { CTAButton } from '../components/CTAButton';
import { ReviewCarousel } from '../components/ReviewCarousel';
import { createCheckoutSession } from '../lib/api';

export function HardPaywallScreen() {
  const { scanResult, realAge, email, setEmail, setPlanToken } = useFlow();
  const [submitting, setSubmitting] = useState(false);
  const [emailDraft, setEmailDraft] = useState(email);
  const [error, setError] = useState<string | null>(null);

  const concerns = useMemo(
    () =>
      (scanResult?.factors ?? [])
        .filter(f => f.severity !== 'healthy')
        .slice(0, 3)
        .map(f => f.name.toLowerCase()),
    [scanResult],
  );

  const concernLine =
    concerns.length === 0
      ? 'Targeted to your scan results'
      : concerns.length === 1
      ? `Targets your ${concerns[0]}`
      : concerns.length === 2
      ? `Targets your ${concerns[0]} and ${concerns[1]}`
      : `Targets your ${concerns.slice(0, -1).join(', ')}, and ${concerns.at(-1)}`;

  if (!scanResult) return <Navigate to="/" replace />;

  const gap = scanResult.skinAge - realAge;

  function emailValid(addr: string) {
    return /.+@.+\..+/.test(addr.trim());
  }

  async function purchase() {
    if (!emailValid(emailDraft)) {
      setError('Enter a valid email so we can send your plan.');
      return;
    }
    setSubmitting(true);
    setError(null);
    try {
      setEmail(emailDraft.trim());
      const { url, planToken } = await createCheckoutSession({
        email: emailDraft.trim(),
        scanResult,
      });
      setPlanToken(planToken);
      window.location.href = url;
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Checkout unavailable.');
      setSubmitting(false);
    }
  }

  return (
    <div className="lume-bg min-h-svh flex flex-col">
      <div className="h-0.5 bg-gradient-to-r from-accent to-accent-dim" />

      <div className="flex-1 overflow-y-auto px-6 pt-9">
        <div className="flex items-center gap-1.5">
          <span className="h-1.5 w-1.5 rounded-full bg-accent animate-pulse" />
          <span className="lume-eyebrow text-accent">FIRST-SCAN OFFER · LIMITED</span>
        </div>

        <h1 className="text-headline text-text mt-3">
          {gap > 0 ? `You're ${gap} years behind.` : 'Your skin is ahead.'}
        </h1>
        <p className="text-title text-text-secondary mt-1.5">
          {gap > 0 ? 'We can close that gap in 8 weeks.' : 'Keep it that way with a plan built for you.'}
        </p>

        <div className="mt-7 bg-surface rounded-card divide-y divide-surface-raised">
          {[
            ['🎯', concernLine],
            ['📋', 'Personalised AM and PM routine'],
            ['🧪', 'Ingredient guide matched to your skin'],
            ['📷', "A locked-in plan you can return to anytime"],
          ].map(([icon, text]) => (
            <div key={text} className="flex items-center gap-3.5 px-4 py-4">
              <span className="text-accent w-5 text-center">{icon}</span>
              <span className="text-body text-text-secondary">{text}</span>
            </div>
          ))}
        </div>

        <div className="mt-7 lume-card-bordered" style={{ boxShadow: 'inset 0 0 0 1px rgba(212,168,78,0.45)' }}>
          <div className="flex items-baseline gap-2">
            <span className="font-extrabold text-text" style={{ fontSize: 38 }}>
              $8.99
            </span>
            <span className="text-body text-text-secondary">one time</span>
            <span className="text-label text-text-tertiary line-through ml-1">$11.99</span>
            <span className="flex-1" />
            <span className="text-eyebrow tracking-widest bg-accent text-bg px-2.5 py-1 rounded">
              25% OFF
            </span>
          </div>
          <p className="text-caption text-accent mt-2.5">No subscription · pay once</p>
          <div className="flex items-center gap-1.5 mt-2 text-caption text-text-tertiary">
            <span className="text-warning">⚠️</span>
            <span>This price expires when you leave this screen</span>
          </div>
        </div>

        {/* Email capture — needed to deliver the plan link */}
        <div className="mt-6">
          <label className="text-label text-text-secondary" htmlFor="email">
            Email — we'll send your plan link here
          </label>
          <input
            id="email"
            type="email"
            inputMode="email"
            autoComplete="email"
            placeholder="you@example.com"
            value={emailDraft}
            onChange={e => setEmailDraft(e.target.value)}
            className="mt-2 w-full bg-surface rounded-card px-4 py-3 text-body text-text outline-none focus:ring-1 focus:ring-accent placeholder:text-text-tertiary"
          />
        </div>

        <div className="mt-7">
          <ReviewCarousel />
        </div>

        <div className="h-8" />
      </div>

      <div
        className="bg-bg/95 backdrop-blur px-6 pt-4"
        style={{ paddingBottom: 'max(3rem, env(safe-area-inset-bottom))' }}
      >
        {error && (
          <p className="text-caption text-danger text-center mb-2">{error}</p>
        )}
        <CTAButton
          label={submitting ? 'Opening checkout…' : 'Claim My 20% Off Plan'}
          disabled={submitting}
          onClick={purchase}
        />
        <p className="text-caption text-text-tertiary text-center mt-3">
          One-time payment · no subscription · generally non-refundable
        </p>
        <p className="text-[11px] text-text-tertiary text-center mt-2 leading-snug">
          Secure checkout via Stripe. By tapping above you agree to our{' '}
          <Link to="/terms" className="underline">Terms</Link> and{' '}
          <Link to="/privacy" className="underline">Privacy Policy</Link>.
        </p>
      </div>
    </div>
  );
}
